/**
 * **Cancel booking** (Board 5 A's button on a booked rental) — asks why, then
 * `POST /visits/:id/rentals/:rid/cancel {reason}`: the rental is *Cancelled*,
 * kept with who, when and why (§2.4 — nothing is deleted), and its stops come
 * off the person's day.
 *
 * No board draws this question (a DEVIATION REQUEST in the slice's report):
 * it is Board 17 F's question in the visit page's popup — a **Why** that must
 * be filled (17 F's 56px box), **Keep it**, and the red button that does it.
 */
import { useState } from 'react'
import type { EquipmentRead, RentalCard } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { SheetField } from './equipment-fields.js'
import { useVisitWrite } from './api.js'
import { CANCEL_VISIT, COMMON, RENTALS } from './copy.js'
import { VisitButton, VisitPopup, VisitTile } from './sheet.js'

export function CancelBookingPopup({ visitId, rental, phone, onClose, onWritten }: {
  visitId: string
  rental: RentalCard
  phone: boolean
  onClose: () => void
  onWritten: (next: EquipmentRead) => void
}) {
  const write = useVisitWrite(visitId)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ready = why.trim().length > 0 && !busy

  async function cancel() {
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      const result = await write('POST', `/rentals/${rental.id}/cancel`, { version: rental.version, reason: why.trim() }, { tab: 'equipment' })
      if (result.tab) onWritten(result.tab as EquipmentRead)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  return (
    <VisitPopup
      width={420}
      phone={phone}
      title={RENTALS.cancelBooking}
      sub={rental.description}
      mark={<VisitTile glyph="close" tone="danger" size={34} />}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{CANCEL_VISIT.keep}</VisitButton>
          <VisitButton size={size} tone="danger" disabled={!ready} onClick={() => void cancel()}>{RENTALS.cancelBooking}</VisitButton>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <SheetField label={COMMON.why} required>
          {({ id, describedBy }) => (
            <textarea id={id} aria-describedby={describedBy} value={why} autoFocus
              onChange={(event) => setWhy(event.target.value)}
              className={cn('block h-14 w-full resize-none rounded-button border border-line bg-surface px-3 py-2.5 leading-normal text-text',
                'focus:border-accent-line focus:outline-none', phone ? 'text-[15px]' : 'text-[13px]')} />
          )}
        </SheetField>
        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </VisitPopup>
  )
}
